import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { GradientText } from '../components/ui/GradientText';
import { Footer } from '../components/shell/Footer';
import { apiClient } from '../network/ApiClient';
import type { LeaderboardRow as ApiLeaderboardRow } from '../types/game';
import { CountryFlag } from '../components/ui/CountryFlag';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import { useTranslation, Trans } from '../i18n/react';

/**
 * LEADERBOARD — global rankings (Figma "LEADERBOARD" frame, 2560×2094
 * export, values ×0.75, DS2 fluid tokens for the shared heading/paragraph
 * styles). No eyebrow and no atmospheric glows on this frame — Figma-exact.
 *
 * Ranked-only (online_ranked; bot games are structurally excluded server-
 * side). There is no rating/ELO system — the Rating column always renders
 * "—". Only page 1 is requested; pagination controls are not part of the
 * approved design yet.
 */

// ── Row shape consumed by the presentational table below ───────────────────

interface LeaderboardRowData {
  name:   string;  // public_name — username/handle if present, else email prefix, else "Player"
  /** Raw country code from the payload — CountryFlag validates it. */
  countryCode: string | null;
  /** Raw avatar key from the payload — PlayerAvatar validates it. */
  avatarKey: string | null;
  rating: string;  // always "—" — no rating system exists
  record: string;  // "wins-losses"
  winPct: number;  // win_percentage, 0.0-100.0
}

/** Exported for unit testing (Record/win% formatting, Rating placeholder, public_name pass-through). */
export function toRowData(row: ApiLeaderboardRow): LeaderboardRowData {
  return {
    name:        row.public_name,
    countryCode: row.country_code ?? null,
    avatarKey:   row.avatar_key ?? null,
    rating:      '—',
    record:      `${row.wins}-${row.losses}`,
    winPct:      row.win_percentage,
  };
}

const PLACEHOLDER_ROWS = 12;

/** Figma win% color rule: green for ≥69%, gold below. */
function winPctColor(pct: number): string {
  return pct >= 69 ? '#4CAF50' : '#D4AF37';
}

/** Medal for top 3, plain rank number after (Figma 🥇🥈🥉 then #CCCCCC numbers). */
function RankCell({ rank }: { rank: number }) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  return (
    <span style={{ fontFamily: DS2.font.heading, fontWeight: 700, fontSize: medal ? 14.7 : 12.2, lineHeight: '18.8px', color: '#CCCCCC' }}>
      {medal ?? rank}
    </span>
  );
}

/** Dim skeleton bar marking a backend-data slot. */
function Bar({ w }: { w: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', width: w, height: 14, borderRadius: 7, background: 'rgba(245,245,245,0.12)', verticalAlign: 'middle' }}
    />
  );
}

// ── Table grid — one template shared by header and rows so columns align ────
// Figma cell widths 85.19 / 632 / 340.5 / 347.29 / 293.42 of 1892, with the
// rank cell holding medal + flag; flag pulled out as its own column here.

const GRID: CSSProperties = {
  display:             'grid',
  gridTemplateColumns: '43px 45px minmax(147px, 1fr) minmax(77px, 173px) minmax(87px, 173px) minmax(73px, 140px)',
  alignItems:          'center',
  columnGap:           11.1,
};

const HEADER_CELL: CSSProperties = {
  fontFamily: DS2.font.body,
  fontWeight: 500,
  fontSize:   17.5,
  lineHeight: '27.2px',
  color:      '#FFC844',
};

const BODY_CELL: CSSProperties = {
  fontFamily: DS2.font.heading,
  fontWeight: 700,
  fontSize:   12.2,
  lineHeight: '18.8px',
  color:      '#CCCCCC',
};

function LeaderboardRow({ rank, row }: { rank: number; row?: LeaderboardRowData }) {
  return (
    <div style={{ ...GRID, minHeight: 46.4 }}>
      <RankCell rank={rank} />
      {/* Country flag (Figma 59×59 slot) — 4:3 flag centered in the square;
          unset/invalid shows the neutral globe placeholder. */}
      <span style={{ width: 36.1, height: 36.1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {row ? <CountryFlag countryCode={row.countryCode} width={32} height={24} /> : null}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8.6, minWidth: 0 }}>
        {/* Figma 38.5px avatar circle — authoritative avatar_key (selected
            preset) or the shared display-name-initial fallback; loading
            keeps the existing skeleton circle. */}
        {row ? (
          <PlayerAvatar
            avatarKey={row.avatarKey}
            name={row.name}
            size={24.3}
            initialStyle={{
              background: 'rgba(245,245,245,0.12)', border: 'none',
              fontFamily: DS2.font.heading, fontWeight: 700, fontSize: 11.7, color: '#CCCCCC',
            }}
          />
        ) : (
          <span aria-hidden="true" style={{ width: 24.3, height: 24.3, borderRadius: '50%', background: 'rgba(245,245,245,0.12)', flexShrink: 0 }} />
        )}
        <span title={row ? row.name : undefined} style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.6, lineHeight: '27.2px', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row ? row.name : <Bar w={130} />}
        </span>
      </span>
      <span style={BODY_CELL}>{row ? row.rating : <Bar w={44} />}</span>
      <span style={{ ...BODY_CELL, fontFamily: DS2.font.body, fontWeight: 400, textAlign: 'center' }}>{row ? row.record : <Bar w={62} />}</span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: 15.6, lineHeight: '18.8px', textAlign: 'center', color: row ? winPctColor(row.winPct) : undefined }}>
        {row ? `${row.winPct}%` : <Bar w={52} />}
      </span>
    </div>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

type LoadStatus = 'loading' | 'success' | 'empty' | 'error';

export function LeaderboardScreen() {
  const { t } = useTranslation('leaderboard');
  const isMobile = useIsMobile();
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<LeaderboardRowData[]>([]);

  const load = useCallback(() => {
    setStatus('loading');
    apiClient.getLeaderboard(1, 50)
      .then(r => {
        setRows(r.rows.map(toRowData));
        setStatus(r.rows.length ? 'success' : 'empty');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    // flex column fills the available viewport height so Footer's
    // marginTop:'auto' sticks flush to the bottom on short pages (see
    // AppShell.tsx's content-wrapper comment).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      <div style={{ paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          {/* Global Leaderboard — Figma screenshot: "Global" white, one gold
              word (the CSS export flattens per-word color to #FFFFFF). The
              catalog owns which word carries the gradient, so es-419 can put
              it on "Clasificación" despite the reversed word order. */}
          <h1
            style={{
              fontSize:      isMobile ? 22 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
              lineHeight:    isMobile ? '26px' : DS2.type.sectionHeading.lineHeight,
              letterSpacing: '-0.02em',
              fontWeight:    700,
              fontFamily:    DS2.font.heading,
              color:         '#FFFFFF',
              margin:        0,
            }}
          >
            {/* Trans replaces the placeholder children with the catalog's
                <accent>…</accent> segment at render. */}
            <Trans t={t} i18nKey="heading" components={{ accent: <GradientText>{null}</GradientText> }} />
          </h1>
          {!isMobile && (
            <p
              style={{
                fontSize:   `clamp(0.93rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                lineHeight: DS2.type.paragraph.lineHeight,
                fontFamily: DS2.font.body,
                fontWeight: 300,
                color:      DS2.color.textBright,
                margin:     '14.7px 0 0',
              }}
            >
              {t('subtitle')}
            </p>
          )}

          {/* Table — horizontal squeeze scrolls (scrollbars hidden app-wide) */}
          <div style={{ marginTop: isMobile ? 20 : 36.7, overflowX: 'auto' }}>
            <div style={{ minWidth: 607 }}>
              {/* Column headers */}
              <div style={GRID}>
                <span style={HEADER_CELL}>{t('col.rank')}</span>
                <span />
                <span style={HEADER_CELL}>{t('col.player')}</span>
                <span style={HEADER_CELL}>{t('col.rating')}</span>
                <span style={{ ...HEADER_CELL, textAlign: 'center' }}>{t('col.record')}</span>
                <span style={{ ...HEADER_CELL, textAlign: 'center' }}>{t('col.winPct')}</span>
              </div>

              {/* Header divider — Figma Vector 15: 1px solid #F5F5F5 */}
              <div style={{ borderTop: '0.75px solid #F5F5F5', marginTop: 11.3 }} />

              {/* Ranking rows — loading shows the existing skeleton, success shows
                  real rows, empty/error replace the table body with a message.
                  Faint separators between rows per the Figma screenshot. */}
              {status === 'loading' && (
                <div style={{ marginTop: 6 }}>
                  {Array.from({ length: PLACEHOLDER_ROWS }, (_, i) => (
                    <div key={i} style={i > 0 ? { borderTop: '1px solid rgba(245,245,245,0.08)' } : undefined}>
                      <LeaderboardRow rank={i + 1} row={undefined} />
                    </div>
                  ))}
                </div>
              )}

              {status === 'success' && (
                <div style={{ marginTop: 6 }}>
                  {rows.map((row, i) => (
                    <div key={i} style={i > 0 ? { borderTop: '1px solid rgba(245,245,245,0.08)' } : undefined}>
                      <LeaderboardRow rank={i + 1} row={row} />
                    </div>
                  ))}
                </div>
              )}

              {status === 'empty' && (
                <div style={{ marginTop: 32, padding: '24px 0', textAlign: 'center', fontFamily: DS2.font.body, fontSize: 16, color: '#CCCCCC' }}>
                  {t('empty')}
                </div>
              )}

              {status === 'error' && (
                <div style={{ marginTop: 32, padding: '24px 0', textAlign: 'center', fontFamily: DS2.font.body, fontSize: 16, color: '#CCCCCC' }}>
                  {t('error')}{' '}
                  <button
                    type="button"
                    onClick={load}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#FFC844',
                      fontFamily: DS2.font.body,
                      fontSize: 16,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {t('retry')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}
